'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { Session } from 'next-auth';

import type { Chapter, TChapterWithoutContent, TWork } from '@/features/fanfic-page/types/fanfic';
import { useChaptersContentQuery } from '@/features/fanfic-page/hooks';
import { consumeReaderOpenedFromWorkPage } from '@/features/fanfic-page/utils/reading-resume.storage';
import overlayStyles from '@/features/reader/styles/reader-overlay.module.css';
import { FullscreenReaderV2 } from './fanfic-page/fullscreen-reader-v2';

interface FanficReaderClientProps {
  fanfic: TWork;
  initialChapterId: string | null;
  session: Session | null;
  workPathPrefix?: '/fanfics' | '/originals';
  initialChaptersWithoutContent: TChapterWithoutContent[];
  initialChaptersWithContent: Chapter[];
  ssrChapterContent?: Chapter;
}

export default function FanficReaderClient({
  fanfic,
  initialChapterId,
  session,
  workPathPrefix = '/fanfics',
  initialChaptersWithoutContent,
  initialChaptersWithContent,
  ssrChapterContent,
}: FanficReaderClientProps) {
  void session;
  const router = useRouter();
  const [selectedChapterId, setSelectedChapterId] = useState<string>(initialChapterId || '');

  // Hook for loading chapters with content (client-side pagination)
  const {
    chapters: chaptersWithContent,
    isLoading: isLoadingChapters,
    fetchChapters: fetchChaptersContent,
    loadMore: loadMoreChapters,
    currentPage,
  } = useChaptersContentQuery({
    workId: fanfic.id.toString(),
    limit: 5,
    initialChapters: initialChaptersWithContent,
    initialPage: 1, // Always start from page 1 (first 5 chapters)
  });

  const handleClose = useCallback(() => {
    const workPageUrl = `${workPathPrefix}/${fanfic.id}`;
    const openedFromWorkPage = consumeReaderOpenedFromWorkPage(fanfic.id.toString());

    // Prefer history back: restores the work page from bfcache/history (faster than push).
    if (openedFromWorkPage && typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }

    router.push(workPageUrl);
  }, [fanfic.id, router, workPathPrefix]);

  const handleChapterChange = (chapterId: string) => {
    setSelectedChapterId(chapterId);
    const newUrl = `${workPathPrefix}/${fanfic.id}/read/${chapterId}`;
    router.replace(newUrl);
  };

  // Handler for loading more chapters in the reader
  const handleLoadMoreChapters = useCallback(
    async (direction: 'next' | 'prev') => {
      if (direction === 'next') {
        await loadMoreChapters();
      } else {
        // For previous chapters, load the previous page
        if (currentPage > 1) {
          await fetchChaptersContent(currentPage - 1);
        }
      }
    },
    [loadMoreChapters, fetchChaptersContent, currentPage],
  );

  // Handler for selecting chapter from dropdown or navigation
  const handleChapterSelect = useCallback(
    async (chapterId: string) => {
      // Check if chapter content is already loaded
      const hasContent = chaptersWithContent.some((ch) => ch.id === chapterId);

      // If content already exists, no need to fetch
      if (hasContent) {
        return;
      }

      // Find the index of selected chapter to calculate which page to load
      const chapterIndex = initialChaptersWithoutContent.findIndex((ch) => ch.id === chapterId);
      if (chapterIndex !== -1) {
        // Calculate page number (each page has 5 chapters)
        const pageToLoad = Math.floor(chapterIndex / 5) + 1;
        await fetchChaptersContent(pageToLoad);
      }
    },
    [initialChaptersWithoutContent, chaptersWithContent, fetchChaptersContent],
  );

  // Load content for initially selected chapter if not in first 5 chapters
  useEffect(() => {
    if (selectedChapterId) {
      handleChapterSelect(selectedChapterId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // If no chapters, show message
  if (initialChaptersWithoutContent.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600 mb-4">У этого фанфика пока нет опубликованных глав</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Вернуться к фанфику
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-50 w-full overflow-hidden bg-[#ffffff] ${overlayStyles.root}`}
      style={{ height: '100dvh', maxHeight: '100dvh' }}
    >
      <FullscreenReaderV2
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        chaptersWithoutContent={initialChaptersWithoutContent}
        chaptersWithContent={chaptersWithContent}
        selectedChapterId={selectedChapterId || initialChaptersWithoutContent[0]?.id || ''}
        setSelectedChapterId={handleChapterChange}
        fanficId={fanfic.id.toString()}
        author={fanfic.authors?.[0] || null}
        views={fanfic.readersCount || fanfic.statistics?.readersCount || 0}
        comments={fanfic.statistics?.commentsCount || 0}
        type="full"
        price={fanfic.price}
        postId={fanfic.id}
        onLoadMoreChapters={handleLoadMoreChapters}
        isLoadingMoreChapters={isLoadingChapters}
        onChapterSelect={handleChapterSelect}
        ssrChapterContent={ssrChapterContent}
        workPathPrefix={workPathPrefix}
        isRating18={fanfic.rating === '18'}
        onLeaveReview={() => {
          router.push(`${workPathPrefix}/${fanfic.id}?tab=reviews&reviewEntry=compose`);
        }}
      />
    </div>
  );
}
